// The course (lessons, letters, phrases, role-plays, bazaar items), loaded once
// from the same content.json the Android app ships.

import { useEffect, useState } from "react";
import type { ContentBundle, PathLesson, PathUnit, PersianLetter, PathItem } from "./types";

let bundle: ContentBundle | null = null;
let loading: Promise<ContentBundle> | null = null;

export function loadContent(): Promise<ContentBundle> {
  if (bundle) return Promise.resolve(bundle);
  loading ??= fetch(`${import.meta.env.BASE_URL}content/content.json`)
    .then((r) => {
      if (!r.ok) throw new Error(`content ${r.status}`);
      return r.json();
    })
    .then((b: ContentBundle) => (bundle = b))
    .catch((e) => {
      loading = null;
      throw e;
    });
  return loading;
}

/** Content for components: null until loaded (the app shell preloads it, so this is rare). */
export function useContent(): ContentBundle | null {
  const [c, setC] = useState(bundle);
  useEffect(() => {
    if (!c) loadContent().then(setC, () => {});
  }, [c]);
  return c;
}

/** Test hook: replace (or with null, drop) the loaded course, e.g. with synthetic Pro units. */
export function setContent(b: ContentBundle | null) {
  bundle = b;
  loading = null;
}

export const content = () => {
  if (!bundle) throw new Error("content not loaded");
  return bundle;
};

export const allLessons = (c: ContentBundle): PathLesson[] => c.units.flatMap((u) => u.lessons);

// ---- levels + unlocking (ios Curriculum.swift; the same rules on Android) ----
// Level 1 is free. In Level 2+ units the first lesson is a free preview and the rest need Pro.
// Pro learners unlock strictly in order; free learners skip over Pro lessons, so after Level 1
// they can play each Pro unit's preview. Lapsed Pro: Pro lessons lock again (stars are kept).
// A finished lesson stays replayable (e.g. a preview done before subscribing), except a Pro-locked one.

export const LEVEL_TITLES: Record<number, string> = { 1: "Level 1 · Beginner", 2: "Level 2 · Everyday Persian", 3: "Level 3 · Speak like a Tehrani" };
export const levelTitle = (n: number) => LEVEL_TITLES[n] ?? `Level ${n}`;
export const unitLevel = (u: PathUnit) => u.level ?? 1;
export const unitOf = (c: ContentBundle, lessonId: string) => c.units.find((u) => u.lessons.some((l) => l.id === lessonId));
export const level1Lessons = (c: ContentBundle) => c.units.filter((u) => unitLevel(u) === 1).flatMap((u) => u.lessons);

/** A lesson only Pro learners can play: in a Level 2+ unit, and not that unit's first (preview) lesson. */
export function requiresPro(c: ContentBundle, id: string): boolean {
  const u = unitOf(c, id);
  return !!u && unitLevel(u) >= 2 && u.lessons[0].id !== id;
}

/** Shown locked behind Pro for this learner (tapping it opens the paywall). */
export const proLocked = (c: ContentBundle, id: string, pro: boolean) => !pro && requiresPro(c, id);

/** The lesson whose completion unlocks `id` for this learner; null for the very first lesson. */
export function unlockedBy(c: ContentBundle, id: string, pro = false): PathLesson | null {
  const all = allLessons(c);
  for (let j = all.findIndex((l) => l.id === id) - 1; j >= 0; j--) if (pro || !requiresPro(c, all[j].id)) return all[j];
  return null;
}

export function isUnlocked(c: ContentBundle, stars: Record<string, number>, id: string, pro = false): boolean {
  if (!allLessons(c).some((l) => l.id === id) || proLocked(c, id, pro)) return false;
  if (stars[id]) return true; // a finished lesson can always be replayed (unless it's Pro-locked)
  const by = unlockedBy(c, id, pro);
  return !by || !!stars[by.id];
}

/** The first lesson this learner can play and hasn't done (Home's "continue", the path's "up next"). */
export const nextLesson = (c: ContentBundle, stars: Record<string, number>, pro = false) =>
  allLessons(c).find((l) => !stars[l.id] && isUnlocked(c, stars, l.id, pro)) || null;

/** The lesson to offer after finishing `id`: the next one in the course this learner can play. */
export function lessonAfter(c: ContentBundle, id: string, pro = false): PathLesson | null {
  const all = allLessons(c);
  return all.slice(all.findIndex((l) => l.id === id) + 1).find((l) => !proLocked(c, l.id, pro)) || null;
}

/** For a free learner with nothing left to play: the level of the first unfinished Pro lesson (for the upsell). */
export function proLevelAhead(c: ContentBundle, stars: Record<string, number>, pro: boolean): number | null {
  if (pro) return null;
  const u = c.units.find((u) => u.lessons.some((l) => requiresPro(c, l.id) && !stars[l.id]));
  return u ? unitLevel(u) : null;
}

// ---- pre-recorded audio keys (same as Android's bundledKeyFile) ----

export const itemAudioKey = (i: PathItem) => `${i.fa}|${i.finglish}`;
export const letterSpokenName = (l: PersianLetter) => l.name.split(" (")[0];
export const letterAudioKey = (l: PersianLetter) => `letter|${l.char}|${letterSpokenName(l)}`;

const ZWJ = "‍";
export const letterForms = (l: PersianLetter) => ({
  isolated: l.char,
  initial: l.nonConnector ? l.char : l.char + ZWJ,
  medial: l.nonConnector ? ZWJ + l.char : ZWJ + l.char + ZWJ,
  final: ZWJ + l.char,
});
